import { TreeRoot } from "@/types";
import DOCUMENT_TYPES from "@/types-docs.json";

export type DocumentTypeItem = {
  label: string;
  value: number | string;
  children?: DocumentTypeItem[];
};

export type DocumentRecord = {
  id: number;
  name: string;
  path: string;
  type?: number | null;
  program?: number | null;
  programName?: string | null;
  project?: number | null;
  projectName?: string | null;
  origin?: "PROGRAM" | "PORTEUR" | "UNKNOWN" | string | null;
  subcontractorId?: number | null;
  subcontractorName?: string | null;
  intervenants?: Array<{
    id: number;
    firstname?: string | null;
    lastname?: string | null;
    fullname?: string | null;
    email?: string | null;
    name?: string | null;
    type?: "intervenant" | "subcontractor" | string | null;
    coworker?: boolean | null;
  }>;
  documentFolders?: Array<{
    id?: number | null;
    name?: string | null;
    scopeType?: "porteur" | "project" | "intervenant" | string | null;
    scopeId?: number | null;
  }>;
  documentFolderIds?: number[];
};

export type DocumentTreeNode = {
  id: string;
  label: string;
  url?: string;
  children?: DocumentTreeNode[];
  isFolder?: boolean;
  deleteFolderTarget?: {
    kind: "custom" | "default";
    id?: number;
    defaultKey?: string;
    scopeType: "porteur" | "project" | "intervenant";
    scopeId: number;
  };
  addFolderTarget?: {
    kind: "porteur" | "project" | "intervenant";
    id: number;
    programId?: number;
    parentFolderId?: number;
  };
};

export type CustomDocumentFolderNode = {
  id: number;
  name: string;
  defaultKey?: string | null;
  documentType?: number | null;
  documentTypes?: number[];
  scopeType: "porteur" | "project" | "intervenant";
  scopeId: number;
  parentId?: number | null;
  position?: number;
  children?: CustomDocumentFolderNode[];
};

export type DocumentFolderTreeResponse = {
  porteur?: Record<string, CustomDocumentFolderNode[]>;
  project?: Record<string, CustomDocumentFolderNode[]>;
  intervenant?: Record<string, CustomDocumentFolderNode[]>;
  hiddenDefaults?: {
    porteur?: Record<string, string[]>;
    project?: Record<string, string[]>;
    intervenant?: Record<string, string[]>;
  };
};

const DOCUMENT_TREE_QUERY_KEYS = [
  "scope",
  "programs",
  "projects",
  "origin",
  "intervenants",
  "categories",
  "types",
  "folderIds",
] as const;

export type BreadcrumbItem = {
  name: string;
  href?: string;
};

const LABEL_BY_TYPE: Record<number, string> = {
  1: "Statuts de l’association",
  2: "Présentation du projet",
  3: "Budget previsional",
  4: "IBAN",
  5: "CV ou biographie",
  7: "Cartographie du terrain (si applicable)",
  8: "Plan d’action / calendrier prévisionnel",
};

const PROJECT_DOC_TYPES = [2, 3, 7, 8];
const INTERVENANT_DOC_TYPES = [1, 5, 4];
const CONTRACTUAL_DOC_TYPES = [25, 26, 27];
const REPORT_DOC_TYPES = [10, 11, 12, 13, 14, 15];
const PROJECTS_ROOT_DEFAULT_KEY = "projects-root";
const INTERVENANTS_ROOT_DEFAULT_KEY = "intervenants-root";
const PROGRAM_ROOT_DEFAULT_KEY_PREFIX = "program-";
const ROOT_FILES_DEFAULT_KEY = "root-files";
const PORTEUR_ROOT_DEFAULT_LABEL = "Documents administratifs";
const TYPE_ORDER = new Map<number, number>();

DOCUMENT_TYPES.forEach((group, groupIndex) => {
  (group.children ?? []).forEach((child, childIndex) => {
    const typeValue = Number(child.value);
    if (!Number.isFinite(typeValue) || TYPE_ORDER.has(typeValue)) return;
    LABEL_BY_TYPE[typeValue] = LABEL_BY_TYPE[typeValue] ?? child.label;
    TYPE_ORDER.set(typeValue, groupIndex * 100 + childIndex);
  });
});

const cleanName = (value?: string | null) =>
  (value ?? "")
    .replace(/^PP\s*-\s*/i, "")
    .replace(/^Prog\s*-\s*/i, "")
    .replace(/^Proj\s*-\s*/i, "")
    .trim();

const isProjectFallbackName = (value?: string | null) =>
  /^(Projet|Projet #\d+)$/i.test((value ?? "").trim());

const isProgramFallbackName = (value?: string | null) =>
  /^Programme #\d+$/i.test((value ?? "").trim());

const isIntervenantFallbackName = (value?: string | null) =>
  /^(Intervenant|Intervenant #\d+)$/i.test((value ?? "").trim());

const getRecordText = (
  record: Record<string, unknown>,
  keys: string[],
): string | null => {
  for (const key of keys) {
    const value = record[key];

    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }

  return null;
};

// const getEligibleIntervenantIds = (
//   intervenants: DocumentRecord["intervenants"] = [],
// ): number[] =>
//   [...new Set(
//     (intervenants ?? [])
//       .filter((intervenant) => Number.isFinite(Number(intervenant?.id)) && Number(intervenant?.id) > 0)
//       .filter((intervenant) => intervenant?.coworker !== true)
//       .filter((intervenant) => intervenant?.type !== "intervenant")
//       .map((intervenant) => Number(intervenant?.id)),
//   )];

const getUrl = (params: Record<string, string | number | Array<string | number> | undefined>) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return;

    if (Array.isArray(value)) {
      if (value.length === 0) return;
      searchParams.set(key, value.join(","));
      return;
    }

    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();
  return query ? `/dashboard/mes-documents?${query}` : "/dashboard/mes-documents";
};

export const normalizeDocumentTypes = (typesDocs: DocumentTypeItem[] = []) =>
  typesDocs.map((item) => ({
    ...item,
    children: (item.children ?? []).map((child) => ({
      ...child,
      label: LABEL_BY_TYPE[Number(child.value)] ?? child.label,
    })),
  }));

export const getDocumentTypeLabel = (documentType: number) =>
  LABEL_BY_TYPE[documentType] ?? `Type ${documentType}`;

const getDocumentSubtype = (doc: Record<string, unknown>): number | null => {
  const candidate = doc.subType ?? doc.idSubcategory ?? doc.type;
  const numeric = Number(candidate);
  return Number.isNaN(numeric) ? null : numeric;
};

const UNKNOWN_DOCUMENT_LABELS = new Set([
  "inconnue",
  "inconnu",
  "unknown",
  "sans catégorie",
  "sans categorie",
]);

export const isUnknownDocumentLabel = (value?: unknown) =>
  typeof value === "string" &&
  UNKNOWN_DOCUMENT_LABELS.has(value.trim().toLowerCase());

const getFolderDisplayName = (
  folder: { name?: string | null } | null | undefined,
  fallbackLabel: string,
) => {
  const label = folder?.name?.trim();
  return label && !isUnknownDocumentLabel(label) ? label : fallbackLabel;
};

export const getDocumentCategoryInfo = (
  typesDocs: DocumentTypeItem[] = [],
  doc: Record<string, unknown>,
): { category: string; subcategory?: string } => {
  const subtype = getDocumentSubtype(doc);

  if (subtype !== null) {
    for (const parent of typesDocs) {
      const child = (parent.children ?? []).find(
        (item) => Number(item.value) === subtype,
      );

      if (child) {
        return {
          category: String(parent.label),
          subcategory: String(child.label),
        };
      }
    }
  }

  const category =
    typeof doc.category === "string" && !isUnknownDocumentLabel(doc.category)
      ? doc.category
      : "Autres";
  const subcategory =
    typeof doc.subcategory === "string" && !isUnknownDocumentLabel(doc.subcategory)
      ? doc.subcategory
      : undefined;

  return { category, subcategory };
};

const getFolderDocumentTypes = (folder: CustomDocumentFolderNode): number[] => {
  const normalized = Array.isArray(folder.documentTypes)
    ? folder.documentTypes.map(Number).filter((value) => Number.isFinite(value) && value > 0)
    : [];

  if (normalized.length > 0) {
    return [...new Set(normalized)];
  }

  if (Number.isFinite(Number(folder.documentType)) && Number(folder.documentType) > 0) {
    return [Number(folder.documentType)];
  }

  return [];
};

const getFolderPrimaryType = (folder: CustomDocumentFolderNode): number | null =>
  getFolderDocumentTypes(folder)[0] ?? null;

export const getDocumentTreeSearchParams = (
  source: URLSearchParams | string,
): URLSearchParams => {
  const params =
    typeof source === "string" ? new URLSearchParams(source) : new URLSearchParams(source.toString());
  const normalizedParams = new URLSearchParams();

  DOCUMENT_TREE_QUERY_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      normalizedParams.set(key, value);
    }
  });

  return normalizedParams;
};

export const getDocumentTreeSearchEntries = (
  source: URLSearchParams | string,
): Array<[string, string]> => [...getDocumentTreeSearchParams(source).entries()].sort();

const getFolderUrlParams = (
  folder: CustomDocumentFolderNode,
  extraParams: Record<string, string | number | Array<string | number> | undefined> = {},
) => {
  const documentTypes = getFolderDocumentTypes(folder);

  return {
    ...extraParams,
    ...(documentTypes.length > 0 ? { categories: documentTypes } : {}),
    folderIds: [folder.id],
  };
};

const findCustomFolderPath = (
  folders: CustomDocumentFolderNode[] = [],
  folderId: number,
): CustomDocumentFolderNode[] | null => {
  for (const folder of folders) {
    if (folder.id === folderId) {
      return [folder];
    }

    const nested = findCustomFolderPath(folder.children ?? [], folderId);
    if (nested) {
      return [folder, ...nested];
    }
  }

  return null;
};

const getScopeFolders = (
  foldersByScope: Record<string, CustomDocumentFolderNode[]> | undefined,
  scopeId?: number | null,
): CustomDocumentFolderNode[] => {
  if (!foldersByScope) {
    return [];
  }

  if (scopeId && foldersByScope[String(scopeId)]) {
    return foldersByScope[String(scopeId)] ?? [];
  }

  const values = Object.values(foldersByScope).filter(Array.isArray);

  if (values.length === 1) {
    return values[0] ?? [];
  }

  return [];
};

const sortCustomFolders = (folders: CustomDocumentFolderNode[]): CustomDocumentFolderNode[] =>
  [...folders].sort((left, right) => {
    const leftType = getFolderPrimaryType(left);
    const rightType = getFolderPrimaryType(right);
    const leftOrder = null === leftType ? Number.POSITIVE_INFINITY : (TYPE_ORDER.get(leftType) ?? Number.POSITIVE_INFINITY);
    const rightOrder = null === rightType ? Number.POSITIVE_INFINITY : (TYPE_ORDER.get(rightType) ?? Number.POSITIVE_INFINITY);

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return (left.name || "Nouveau dossier").localeCompare(right.name || "Nouveau dossier", "fr");
  });

const filterFoldersByDocumentTypes = (
  folders: CustomDocumentFolderNode[] = [],
  allowedTypes: number[] = [],
): CustomDocumentFolderNode[] => {
  const normalizedAllowedTypes = [...new Set(
    allowedTypes
      .map(Number)
      .filter((value) => Number.isFinite(value) && value > 0),
  )];

  if (normalizedAllowedTypes.length === 0) {
    return [];
  }

  return folders.flatMap((folder) => {
    const folderTypes = getFolderDocumentTypes(folder);
    const filteredChildren = filterFoldersByDocumentTypes(folder.children ?? [], normalizedAllowedTypes);
    const folderMatches = folderTypes.some((type) => normalizedAllowedTypes.includes(type));

    if (!folderMatches && filteredChildren.length === 0) {
      return [];
    }

    return [{
      ...folder,
      children: folderMatches ? folder.children : filteredChildren,
    }];
  });
};

const isDefaultHidden = (
  hiddenDefaults: DocumentFolderTreeResponse["hiddenDefaults"],
  scopeType: "porteur" | "project" | "intervenant",
  scopeId: number,
  defaultKey: string,
): boolean => (hiddenDefaults?.[scopeType]?.[String(scopeId)] ?? []).includes(defaultKey);

const hasExactDocumentTypes = (
  folder: CustomDocumentFolderNode,
  expectedTypes: number[],
): boolean => {
  const folderTypes = [...new Set(getFolderDocumentTypes(folder))].sort((a, b) => a - b);
  const normalizedExpected = [...new Set(expectedTypes)].sort((a, b) => a - b);

  return (
    folderTypes.length === normalizedExpected.length &&
    folderTypes.every((value, index) => value === normalizedExpected[index])
  );
};

const matchesDefaultOverrideFolder = (
  folder: CustomDocumentFolderNode,
  defaultKey: string,
  documentTypes: number[] = [],
): boolean => {
  if (null !== folder.defaultKey && undefined !== folder.defaultKey) {
    return folder.defaultKey === defaultKey;
  }

  if (
    "root" === defaultKey ||
    PROJECTS_ROOT_DEFAULT_KEY === defaultKey ||
    INTERVENANTS_ROOT_DEFAULT_KEY === defaultKey
  ) {
    return false;
  }

  return hasExactDocumentTypes(folder, documentTypes);
};

const consumeDefaultOverrideFolder = (
  folders: CustomDocumentFolderNode[] = [],
  matcher: (folder: CustomDocumentFolderNode) => boolean,
): {
  overrideFolder: CustomDocumentFolderNode | null;
  remainingFolders: CustomDocumentFolderNode[];
} => {
  const index = folders.findIndex((folder) => matcher(folder));

  if (index < 0) {
    return {
      overrideFolder: null,
      remainingFolders: folders,
    };
  }

  return {
    overrideFolder: folders[index] ?? null,
    remainingFolders: folders.filter((_, folderIndex) => folderIndex !== index),
  };
};

const consumePersistedDefaultOverrideFolder = (
  folders: CustomDocumentFolderNode[] = [],
  defaultKey: string,
  documentTypes: number[] = [],
  defaultHidden = false,
) =>
  consumeDefaultOverrideFolder(
    folders,
    (folder) =>
      folder.defaultKey === defaultKey ||
      (defaultHidden && matchesDefaultOverrideFolder(folder, defaultKey, documentTypes)),
  );

const partitionFolders = (
  folders: CustomDocumentFolderNode[] = [],
  matcher: (folder: CustomDocumentFolderNode) => boolean,
): {
  matchedFolders: CustomDocumentFolderNode[];
  remainingFolders: CustomDocumentFolderNode[];
} => ({
  matchedFolders: folders.filter((folder) => matcher(folder)),
  remainingFolders: folders.filter((folder) => !matcher(folder)),
});

const findDefaultOverrideFolder = (
  folders: CustomDocumentFolderNode[] = [],
  matcher: (folder: CustomDocumentFolderNode) => boolean,
): CustomDocumentFolderNode | null =>
  folders.find((folder) => matcher(folder)) ?? null;

const isImageDocument = (doc: Partial<DocumentRecord>) => {
  const fileName = doc.name?.toLowerCase() ?? "";
  const extension = fileName.split(".").pop() ?? "";

  return fileName === "blob" || ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension);
};

const buildFolderPathLabel = (path: CustomDocumentFolderNode[] = []) =>
  path.map((folder) => folder.name || "Nouveau dossier").join(" / ");

const buildScopedFolderPathLabel = (
  path: CustomDocumentFolderNode[] = [],
  rootLabel?: string | null,
  rootDefaultKeys: string[] = [],
) => {
  if (path.length === 0) {
    return "";
  }

  const normalizedRootLabel = (rootLabel ?? "").trim().toLowerCase();
  const firstFolderLabel = (path[0]?.name ?? "").trim().toLowerCase();
  const firstDefaultKey = path[0]?.defaultKey ?? null;
  const shouldSkipFirstFolder =
    (firstDefaultKey && rootDefaultKeys.includes(firstDefaultKey)) ||
    (normalizedRootLabel && firstFolderLabel === normalizedRootLabel);

  if (shouldSkipFirstFolder) {
    return buildFolderPathLabel(path.slice(1));
  }

  return buildFolderPathLabel(path);
};

const getDocumentProgramName = (doc: Partial<DocumentRecord> & Record<string, unknown>) =>
  getRecordText(doc, ["programName", "program_name", "programLabel", "program_label"]);

const getDocumentProjectName = (doc: Partial<DocumentRecord> & Record<string, unknown>) =>
  getRecordText(doc, ["projectName", "project_name", "projectLabel", "project_label"]);

const getLinkedFolderMetas = (doc: Partial<DocumentRecord> & Record<string, unknown>) => {
  const documentFolders = Array.isArray(doc.documentFolders) ? doc.documentFolders : [];

  return documentFolders
    .map((folder) => ({
      id: Number(folder?.id ?? 0),
      scopeType: typeof folder?.scopeType === "string" ? folder.scopeType : null,
      scopeId: Number(folder?.scopeId ?? 0),
    }))
    .filter((folder) => Number.isFinite(folder.id) && folder.id > 0);
};

const getEligibleIntervenantIds = (
  intervenants: DocumentRecord["intervenants"],
): number[] =>
  Array.isArray(intervenants)
    ? intervenants
      .filter((intervenant) => intervenant?.coworker !== true)
      .filter((intervenant) => intervenant?.type !== "intervenant")
      .map((intervenant) => Number(intervenant?.id))
      .filter((value) => Number.isFinite(value) && value > 0)
    : [];

const getIntervenantDisplayName = (
  intervenant?: NonNullable<DocumentRecord["intervenants"]>[number] | null,
): string | null => {
  const name =
    intervenant?.fullname?.trim()
    || intervenant?.name?.trim()
    || [intervenant?.firstname, intervenant?.lastname].filter(Boolean).join(" ").trim();

  if (!name || isIntervenantFallbackName(name)) {
    return null;
  }

  return name;
};

const getDefaultFolderOverrideLabel = ({
  customFolders,
  hiddenDefaults,
  scopeType,
  scopeId,
  defaultKey,
  documentTypes,
}: {
  customFolders?: DocumentFolderTreeResponse;
  hiddenDefaults?: DocumentFolderTreeResponse["hiddenDefaults"];
  scopeType: "porteur" | "project" | "intervenant";
  scopeId: number;
  defaultKey: string;
  documentTypes?: number[];
}): string | null => {
  if (!isDefaultHidden(hiddenDefaults, scopeType, scopeId, defaultKey)) {
    return null;
  }

  const overrideFolder = findDefaultOverrideFolder(
    getScopeFolders(customFolders?.[scopeType], scopeId),
    (folder) => matchesDefaultOverrideFolder(folder, defaultKey, documentTypes ?? []),
  );

  const overrideLabel = overrideFolder?.name?.trim() || null;
  if (isUnknownDocumentLabel(overrideLabel)) {
    return null;
  }
  if (
    scopeType === "porteur" &&
    defaultKey === PROJECTS_ROOT_DEFAULT_KEY &&
    overrideLabel?.toLowerCase() === "mes projets"
  ) {
    return "Mes Programmes";
  }

  if (
    scopeType === "project" &&
    defaultKey === "root" &&
    isProjectFallbackName(overrideLabel)
  ) {
    return null;
  }

  if (
    scopeType === "intervenant" &&
    defaultKey === "root" &&
    isIntervenantFallbackName(overrideLabel)
  ) {
    return null;
  }

  return overrideLabel;
};

const getDefaultFolderDisplayLabel = ({
  customFolders,
  scopeType,
  scopeId,
  defaultKey,
  documentTypes,
  fallbackLabel,
}: {
  customFolders?: DocumentFolderTreeResponse;
  scopeType: "porteur" | "project" | "intervenant";
  scopeId: number;
  defaultKey: string;
  documentTypes?: number[];
  fallbackLabel: string;
}): string =>
  getDefaultFolderOverrideLabel({
    customFolders,
    hiddenDefaults: customFolders?.hiddenDefaults,
    scopeType,
    scopeId,
    defaultKey,
    documentTypes,
  }) ?? fallbackLabel;

const getProgramDefaultKey = (programId: number) =>
  `${PROGRAM_ROOT_DEFAULT_KEY_PREFIX}${programId}`;

const getProgramDisplayLabel = ({
  customFolders,
  currentUserId,
  programId,
  fallbackLabel,
}: {
  customFolders?: DocumentFolderTreeResponse;
  currentUserId?: number | null;
  programId: number;
  fallbackLabel: string;
}) =>
  getDefaultFolderDisplayLabel({
    customFolders,
    scopeType: "porteur",
    scopeId: currentUserId ?? 0,
    defaultKey: getProgramDefaultKey(programId),
    fallbackLabel,
  });

const hasSameNumberSet = (left: number[] = [], right: number[] = []) => {
  const normalizedLeft = [...new Set(left)].sort((a, b) => a - b);
  const normalizedRight = [...new Set(right)].sort((a, b) => a - b);

  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((value, index) => value === normalizedRight[index])
  );
};

const getDefaultCategoryDisplayLabel = ({
  customFolders,
  scopeType,
  scopeId,
  categoryIds,
  fallbackLabel,
}: {
  customFolders?: DocumentFolderTreeResponse;
  scopeType: "porteur" | "project" | "intervenant";
  scopeId: number;
  categoryIds: number[];
  fallbackLabel: string;
}) => {
  const normalizedCategoryIds = categoryIds
    .map(Number)
    .filter((value) => Number.isFinite(value) && value > 0);
  const firstCategoryId = normalizedCategoryIds[0] ?? 0;

  if (
    scopeType === "project" &&
    normalizedCategoryIds.length === 1 &&
    PROJECT_DOC_TYPES.includes(firstCategoryId)
  ) {
    return getDefaultFolderDisplayLabel({
      customFolders,
      scopeType,
      scopeId,
      defaultKey: `type-${firstCategoryId}`,
      documentTypes: [firstCategoryId],
      fallbackLabel,
    });
  }

  if (
    (scopeType === "porteur" || scopeType === "intervenant") &&
    normalizedCategoryIds.length === 1 &&
    INTERVENANT_DOC_TYPES.includes(firstCategoryId)
  ) {
    return getDefaultFolderDisplayLabel({
      customFolders,
      scopeType,
      scopeId,
      defaultKey: `type-${firstCategoryId}`,
      documentTypes: [firstCategoryId],
      fallbackLabel,
    });
  }

  if (
    (scopeType === "porteur" || scopeType === "intervenant") &&
    hasSameNumberSet(normalizedCategoryIds, CONTRACTUAL_DOC_TYPES)
  ) {
    return getDefaultFolderDisplayLabel({
      customFolders,
      scopeType,
      scopeId,
      defaultKey: "contractuel",
      documentTypes: CONTRACTUAL_DOC_TYPES,
      fallbackLabel: "Contractuel",
    });
  }

  if (
    (scopeType === "porteur" || scopeType === "intervenant") &&
    hasSameNumberSet(normalizedCategoryIds, REPORT_DOC_TYPES)
  ) {
    return getDefaultFolderDisplayLabel({
      customFolders,
      scopeType,
      scopeId,
      defaultKey: "rapport",
      documentTypes: REPORT_DOC_TYPES,
      fallbackLabel: "Rapport",
    });
  }

  return fallbackLabel;
};

export const getDocumentTreeFolderLabel = ({
  doc,
  customFolders,
  currentUserId,
}: {
  doc: Partial<DocumentRecord> & Record<string, unknown>;
  customFolders?: DocumentFolderTreeResponse;
  currentUserId?: number | null;
}): string => {
  const customFolderIds = Array.isArray(doc.documentFolderIds)
    ? doc.documentFolderIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)
    : [];
  const linkedFolders = getLinkedFolderMetas(doc);

  const projectId = Number(doc.project ?? 0);
  const intervenantIds = getEligibleIntervenantIds(doc.intervenants);
  const subtype = getDocumentSubtype(doc);

  for (const linkedFolder of linkedFolders) {
    if (linkedFolder.scopeType === "project" && linkedFolder.scopeId > 0) {
      const projectPath = findCustomFolderPath(
        getScopeFolders(customFolders?.project, linkedFolder.scopeId),
        linkedFolder.id,
      );
      if (projectPath?.length) {
        return buildFolderPathLabel(projectPath);
      }
    }

    if (linkedFolder.scopeType === "intervenant" && linkedFolder.scopeId > 0) {
      const intervenantPath = findCustomFolderPath(
        getScopeFolders(customFolders?.intervenant, linkedFolder.scopeId),
        linkedFolder.id,
      );
      if (intervenantPath?.length) {
        return buildFolderPathLabel(intervenantPath);
      }
    }

    if (linkedFolder.scopeType === "porteur" && currentUserId) {
      const porteurPath = findCustomFolderPath(
        getScopeFolders(customFolders?.porteur, currentUserId),
        linkedFolder.id,
      );
      if (porteurPath?.length) {
        return buildFolderPathLabel(porteurPath);
      }
    }
  }

  for (const folderId of customFolderIds) {
    if (projectId > 0) {
      const projectPath = findCustomFolderPath(
        getScopeFolders(customFolders?.project, projectId),
        folderId,
      );
      if (projectPath?.length) {
        return buildFolderPathLabel(projectPath);
      }
    }

    for (const intervenantId of intervenantIds) {
      const intervenantPath = findCustomFolderPath(
        getScopeFolders(customFolders?.intervenant, intervenantId),
        folderId,
      );
      if (intervenantPath?.length) {
        return buildFolderPathLabel(intervenantPath);
      }
    }

    if (currentUserId) {
      const porteurPath = findCustomFolderPath(
        getScopeFolders(customFolders?.porteur, currentUserId),
        folderId,
      );
      if (porteurPath?.length) {
        return buildFolderPathLabel(porteurPath);
      }
    }
  }

  if (projectId > 0) {
    if (isImageDocument(doc)) {
      return getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "project",
        scopeId: projectId,
        defaultKey: "photos",
        fallbackLabel: "Photos",
      });
    }

    if (subtype && PROJECT_DOC_TYPES.includes(subtype)) {
      return getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "project",
        scopeId: projectId,
        defaultKey: `type-${subtype}`,
        documentTypes: [subtype],
        fallbackLabel: getDocumentTypeLabel(subtype),
      });
    }
  }

  if (intervenantIds.length > 0) {
    if (subtype && INTERVENANT_DOC_TYPES.includes(subtype)) {
      return getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "intervenant",
        scopeId: intervenantIds[0]!,
        defaultKey: `type-${subtype}`,
        documentTypes: [subtype],
        fallbackLabel: getDocumentTypeLabel(subtype),
      });
    }

    if (subtype && CONTRACTUAL_DOC_TYPES.includes(subtype)) {
      return getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "intervenant",
        scopeId: intervenantIds[0]!,
        defaultKey: "contractuel",
        documentTypes: CONTRACTUAL_DOC_TYPES,
        fallbackLabel: "Contractuel",
      });
    }

    if (subtype && REPORT_DOC_TYPES.includes(subtype)) {
      return getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "intervenant",
        scopeId: intervenantIds[0]!,
        defaultKey: "rapport",
        documentTypes: REPORT_DOC_TYPES,
        fallbackLabel: "Rapport",
      });
    }
  }

  if (subtype && INTERVENANT_DOC_TYPES.includes(subtype)) {
    return getDefaultFolderDisplayLabel({
      customFolders,
      scopeType: "porteur",
      scopeId: currentUserId ?? 0,
      defaultKey: `type-${subtype}`,
      documentTypes: [subtype],
      fallbackLabel: getDocumentTypeLabel(subtype),
    });
  }

  if (subtype && CONTRACTUAL_DOC_TYPES.includes(subtype)) {
    return getDefaultFolderDisplayLabel({
      customFolders,
      scopeType: "porteur",
      scopeId: currentUserId ?? 0,
      defaultKey: "contractuel",
      documentTypes: CONTRACTUAL_DOC_TYPES,
      fallbackLabel: "Contractuel",
    });
  }

  if (subtype && REPORT_DOC_TYPES.includes(subtype)) {
    return getDefaultFolderDisplayLabel({
      customFolders,
      scopeType: "porteur",
      scopeId: currentUserId ?? 0,
      defaultKey: "rapport",
      documentTypes: REPORT_DOC_TYPES,
      fallbackLabel: "Rapport",
    });
  }

  return "Autres documents";
};

export const getDocumentTreePathLabel = ({
  doc,
  carrierTree = [],
  customFolders,
  currentUserId,
}: {
  doc: Partial<DocumentRecord> & Record<string, unknown>;
  carrierTree?: TreeRoot[];
  customFolders?: DocumentFolderTreeResponse;
  currentUserId?: number | null;
}): string => {
  const folderLabel = getDocumentTreeFolderLabel({
    doc,
    customFolders,
    currentUserId,
  });
  const linkedFolder = getLinkedFolderMetas(doc)[0] ?? null;
  const customFolderIds = Array.isArray(doc.documentFolderIds)
    ? doc.documentFolderIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)
    : [];

  const projectId = Number(doc.project ?? 0);
  const programId = Number(doc.program ?? 0);
  const documentProgramName = cleanName(getDocumentProgramName(doc));
  const documentProjectName = cleanName(getDocumentProjectName(doc));
  const intervenantIds = getEligibleIntervenantIds(doc.intervenants);

  const allPrograms = carrierTree.flatMap((carrier) => carrier.programs ?? []);
  const program = allPrograms.find((candidate) => Number(candidate.id) === programId);
  const project =
    program?.projects?.find((candidate) => Number(candidate.id) === projectId) ??
    allPrograms
      .flatMap((candidate) => candidate.projects ?? [])
      .find((candidate) => Number(candidate.id) === projectId);

  if (linkedFolder?.scopeType === "intervenant" && linkedFolder.scopeId > 0) {
    const allIntervenants = carrierTree.flatMap((carrier) => carrier.intervenants ?? []);
    const intervenant = allIntervenants.find(
      (candidate) => Number(candidate.id) === linkedFolder.scopeId,
    );
    const linkedDocumentSubcontractorName =
      Number(doc.subcontractorId ?? 0) === linkedFolder.scopeId
        ? cleanName(doc.subcontractorName)
        : "";
    const intervenantName =
      getIntervenantDisplayName(intervenant) ||
      (
        linkedDocumentSubcontractorName && !isIntervenantFallbackName(linkedDocumentSubcontractorName)
          ? linkedDocumentSubcontractorName
          : `Intervenant #${linkedFolder.scopeId}`
      );
    const intervenantPath = findCustomFolderPath(
      getScopeFolders(customFolders?.intervenant, linkedFolder.scopeId),
      linkedFolder.id,
    );
    const intervenantRootLabel = getDefaultFolderDisplayLabel({
      customFolders,
      scopeType: "intervenant",
      scopeId: linkedFolder.scopeId,
      defaultKey: "root",
      fallbackLabel: intervenantName,
    });
    const intervenantDisplayLabel = intervenantRootLabel;

    if (
      (intervenantPath?.length ?? 0) === 1
      && (intervenantPath?.[0]?.defaultKey ?? null) === "root"
    ) {
      return ["Dossier intervenant", intervenantDisplayLabel]
        .filter(Boolean)
        .join(" / ");
    }

    return [
      "Dossier intervenant",
      intervenantDisplayLabel,
      buildScopedFolderPathLabel(intervenantPath ?? [], intervenantDisplayLabel, ["root"]),
    ]
      .filter(Boolean)
      .join(" / ");
  }

  if (linkedFolder?.scopeType === "porteur") {
    const porteurPath = currentUserId
      ? findCustomFolderPath(getScopeFolders(customFolders?.porteur, currentUserId), linkedFolder.id)
      : null;
    const porteurRootLabel = getDefaultFolderDisplayLabel({
      customFolders,
      scopeType: "porteur",
      scopeId: currentUserId ?? 0,
      defaultKey: "root",
      fallbackLabel: PORTEUR_ROOT_DEFAULT_LABEL,
    });
    const projectsRootLabel = getDefaultFolderDisplayLabel({
      customFolders,
      scopeType: "porteur",
      scopeId: currentUserId ?? 0,
      defaultKey: PROJECTS_ROOT_DEFAULT_KEY,
      fallbackLabel: "Mes Programmes",
    });
    const intervenantsRootLabel = getDefaultFolderDisplayLabel({
      customFolders,
      scopeType: "porteur",
      scopeId: currentUserId ?? 0,
      defaultKey: INTERVENANTS_ROOT_DEFAULT_KEY,
      fallbackLabel: "Dossier intervenant",
    });

    if (porteurPath?.length) {
      const rootFolder = porteurPath[0];
      const isDirectDefaultRootFolder = porteurPath.length === 1;

      if (rootFolder?.defaultKey === PROJECTS_ROOT_DEFAULT_KEY) {
        if (isDirectDefaultRootFolder) {
          return [projectsRootLabel, buildScopedFolderPathLabel(porteurPath, projectsRootLabel, [PROJECTS_ROOT_DEFAULT_KEY])]
            .filter(Boolean)
            .join(" / ");
        }

        return [projectsRootLabel, buildScopedFolderPathLabel(porteurPath, projectsRootLabel, [PROJECTS_ROOT_DEFAULT_KEY])].filter(Boolean).join(" / ");
      }

      if (rootFolder?.defaultKey === INTERVENANTS_ROOT_DEFAULT_KEY) {
        if (isDirectDefaultRootFolder) {
          return [intervenantsRootLabel, buildScopedFolderPathLabel(porteurPath, intervenantsRootLabel, [INTERVENANTS_ROOT_DEFAULT_KEY])]
            .filter(Boolean)
            .join(" / ");
        }

        return [intervenantsRootLabel, buildScopedFolderPathLabel(porteurPath, intervenantsRootLabel, [INTERVENANTS_ROOT_DEFAULT_KEY])].filter(Boolean).join(" / ");
      }

      if ((rootFolder?.defaultKey ?? "").startsWith(PROGRAM_ROOT_DEFAULT_KEY_PREFIX)) {
        const programId = Number((rootFolder?.defaultKey ?? "").slice(PROGRAM_ROOT_DEFAULT_KEY_PREFIX.length));
        const program = allPrograms.find((candidate) => Number(candidate.id) === programId);
        const programLabel = getProgramDisplayLabel({
          customFolders,
          currentUserId,
          programId,
          fallbackLabel: cleanName(program?.name),
        });

        if (isDirectDefaultRootFolder) {
          return ["Mes Programmes", programLabel, buildScopedFolderPathLabel(porteurPath, programLabel, [getProgramDefaultKey(programId)])]
            .filter(Boolean)
            .join(" / ");
        }

        return ["Mes Programmes", programLabel, buildScopedFolderPathLabel(porteurPath, programLabel, [getProgramDefaultKey(programId)])].filter(Boolean).join(" / ");
      }

      if (rootFolder?.defaultKey === "root" && isDirectDefaultRootFolder) {
        return [porteurRootLabel, buildScopedFolderPathLabel(porteurPath, porteurRootLabel, ["root"])]
          .filter(Boolean)
          .join(" / ");
      }

      if (rootFolder?.defaultKey === "root") {
        return [porteurRootLabel, buildScopedFolderPathLabel(porteurPath, porteurRootLabel, ["root"])]
          .filter(Boolean)
          .join(" / ");
      }

      if (!rootFolder?.defaultKey) {
        return buildFolderPathLabel(porteurPath);
      }

      return [
        porteurRootLabel,
        buildFolderPathLabel(porteurPath),
      ]
        .filter(Boolean)
        .join(" / ");
    }

    return folderLabel;
  }

  if (linkedFolder?.scopeType === "project" && linkedFolder.scopeId > 0) {
    const projectPath = findCustomFolderPath(
      getScopeFolders(customFolders?.project, linkedFolder.scopeId),
      linkedFolder.id,
    );
    const linkedProject =
      allPrograms
        .flatMap((candidate) => candidate.projects ?? [])
        .find((candidate) => Number(candidate.id) === linkedFolder.scopeId) ?? project;

    if (linkedProject || documentProjectName || linkedFolder.scopeId > 0) {
      const resolvedProgram = allPrograms.find((candidate) =>
        (candidate.projects ?? []).some(
          (candidateProject) => Number(candidateProject.id) === Number(linkedProject?.id ?? linkedFolder.scopeId),
        ),
      ) ?? program;
      const resolvedProgramId = Number(resolvedProgram?.id ?? programId ?? 0);
      const projectRootLabel = getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "project",
        scopeId: linkedFolder.scopeId,
        defaultKey: "root",
        fallbackLabel: cleanName(linkedProject?.name) || documentProjectName || `Projet #${linkedFolder.scopeId}`,
      });
      const projectDisplayLabel = projectRootLabel;
      const programLabel = getProgramDisplayLabel({
        customFolders,
        currentUserId,
        programId: resolvedProgramId,
        fallbackLabel: cleanName(resolvedProgram?.name) || documentProgramName || (resolvedProgramId > 0 ? `Programme #${resolvedProgramId}` : ""),
      });

      if (
        (projectPath?.length ?? 0) === 1
        && (projectPath?.[0]?.defaultKey ?? null) === "root"
      ) {
        return [
          "Mes Programmes",
          programLabel,
          projectDisplayLabel,
        ]
          .filter(Boolean)
          .join(" / ");
      }

      return [
        "Mes Programmes",
        programLabel,
        projectDisplayLabel,
        buildScopedFolderPathLabel(projectPath ?? [], projectDisplayLabel, ["root"]),
      ]
        .filter(Boolean)
        .join(" / ");
    }
  }

  for (const folderId of customFolderIds) {
    const porteurPath = currentUserId
      ? findCustomFolderPath(
        getScopeFolders(customFolders?.porteur, currentUserId),
        folderId,
      )
      : null;

    if (!porteurPath?.length) {
      continue;
    }

    const rootFolder = porteurPath[0];

    if (rootFolder?.defaultKey === PROJECTS_ROOT_DEFAULT_KEY) {
      const projectsRootLabel = getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: PROJECTS_ROOT_DEFAULT_KEY,
        fallbackLabel: "Mes Programmes",
      });

      return [projectsRootLabel, buildScopedFolderPathLabel(porteurPath, projectsRootLabel, [PROJECTS_ROOT_DEFAULT_KEY])]
        .filter(Boolean)
        .join(" / ");
    }

    if (rootFolder?.defaultKey === INTERVENANTS_ROOT_DEFAULT_KEY) {
      const intervenantsRootLabel = getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: INTERVENANTS_ROOT_DEFAULT_KEY,
        fallbackLabel: "Dossier intervenant",
      });

      return [intervenantsRootLabel, buildScopedFolderPathLabel(porteurPath, intervenantsRootLabel, [INTERVENANTS_ROOT_DEFAULT_KEY])]
        .filter(Boolean)
        .join(" / ");
    }

    if ((rootFolder?.defaultKey ?? "").startsWith(PROGRAM_ROOT_DEFAULT_KEY_PREFIX)) {
      const rootProgramId = Number(
        (rootFolder?.defaultKey ?? "").slice(PROGRAM_ROOT_DEFAULT_KEY_PREFIX.length),
      );
      const rootProgram = allPrograms.find(
        (candidate) => Number(candidate.id) === rootProgramId,
      );
      const programLabel = getProgramDisplayLabel({
        customFolders,
        currentUserId,
        programId: rootProgramId,
        fallbackLabel: cleanName(rootProgram?.name),
      });

      return [
        "Mes Programmes",
        programLabel,
        buildScopedFolderPathLabel(porteurPath, programLabel, [getProgramDefaultKey(rootProgramId)]),
      ]
        .filter(Boolean)
        .join(" / ");
    }

    if (!rootFolder?.defaultKey) {
      return buildFolderPathLabel(porteurPath);
    }

    if (rootFolder?.defaultKey === "root") {
      const porteurRootLabel = getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: "root",
        fallbackLabel: PORTEUR_ROOT_DEFAULT_LABEL,
      });

      return [porteurRootLabel, buildScopedFolderPathLabel(porteurPath, porteurRootLabel, ["root"])]
        .filter(Boolean)
        .join(" / ");
    }

    return [
      getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: "root",
        fallbackLabel: PORTEUR_ROOT_DEFAULT_LABEL,
      }),
      buildFolderPathLabel(porteurPath),
    ]
      .filter(Boolean)
      .join(" / ");
  }

  if (project || projectId > 0) {
    const resolvedProgram =
      program ??
      allPrograms.find((candidate) =>
        (candidate.projects ?? []).some(
          (candidateProject) => Number(candidateProject.id) === Number(project?.id ?? projectId),
        ),
      );
    const resolvedProgramId = Number(resolvedProgram?.id ?? programId ?? 0);
    const programLabel = getProgramDisplayLabel({
      customFolders,
      currentUserId,
      programId: resolvedProgramId,
      fallbackLabel: cleanName(resolvedProgram?.name) || documentProgramName || (resolvedProgramId > 0 ? `Programme #${resolvedProgramId}` : ""),
    });

    const projectLabel = getDefaultFolderDisplayLabel({
      customFolders,
      scopeType: "project",
      scopeId: Number(project?.id ?? projectId),
      defaultKey: "root",
      fallbackLabel: cleanName(project?.name) || documentProjectName || `Projet #${projectId}`,
    });

    return [
      "Mes Programmes",
      programLabel,
      projectLabel,
      folderLabel,
    ]
      .filter(Boolean)
      .join(" / ");
  }

  if (intervenantIds.length > 0) {
    const allIntervenants = carrierTree.flatMap((carrier) => carrier.intervenants ?? []);
    const intervenant = allIntervenants.find(
      (candidate) => Number(candidate.id) === intervenantIds[0],
    );
    const intervenantName = getIntervenantDisplayName(intervenant) || `Intervenant #${intervenantIds[0]}`;

    const intervenantLabel = getDefaultFolderDisplayLabel({
      customFolders,
      scopeType: "intervenant",
      scopeId: intervenantIds[0] ?? 0,
      defaultKey: "root",
      fallbackLabel: intervenantName,
    });

    return ["Dossier intervenant", intervenantLabel, folderLabel]
      .filter(Boolean)
      .join(" / ");
  }

  return [PORTEUR_ROOT_DEFAULT_LABEL, folderLabel].filter(Boolean).join(" / ");
};

const buildCustomFolderNodes = ({
  kind,
  ownerId,
  programId,
  folders,
  extraParams = {},
}: {
  kind: "porteur" | "project" | "intervenant";
  ownerId: number;
  programId?: number;
  folders?: CustomDocumentFolderNode[];
  extraParams?: Record<string, string | number | Array<string | number> | undefined>;
}): DocumentTreeNode[] =>
  (folders ?? []).map((folder) => ({
    id: `custom-folder-${folder.id}`,
    label: folder.name || "Nouveau dossier",
    url:
      kind === "project"
        ? getUrl(getFolderUrlParams(folder, {
          programs: programId ? [programId] : undefined,
          projects: [ownerId],
        }))
        : kind === "porteur"
          ? getUrl(getFolderUrlParams(folder, extraParams))
          : getUrl(getFolderUrlParams(folder, {
            origin: "PROGRAM",
            intervenants: [ownerId],
          })),
    children: buildCustomFolderNodes({
      kind,
      ownerId,
      programId,
      folders: folder.children,
      extraParams,
    }),
    isFolder: true,
    deleteFolderTarget: {
      kind: "custom",
      id: folder.id,
      scopeType: kind,
      scopeId: ownerId,
    },
    addFolderTarget: {
      kind,
      id: ownerId,
      programId,
      parentFolderId: folder.id,
    },
  }));

export const buildDocumentTree = ({
  documentsData,
  carrierTree = [],
  customFolders,
  currentUserId,
  activeProgramId,
  activeProjectId,
  activeIntervenantId,
  hideIntervenantFolder = false,
  directIntervenantsOnly = false,
}: {
  documentsData?: Record<string, DocumentRecord[]> | null;
  carrierTree?: TreeRoot[];
  customFolders?: DocumentFolderTreeResponse;
  currentUserId?: number | null;
  activeProgramId?: number | null;
  activeProjectId?: number | null;
  activeIntervenantId?: number | null;
  hideIntervenantFolder?: boolean;
  directIntervenantsOnly?: boolean;
}): DocumentTreeNode[] => {
  const programsById = new Map<number, { name: string; projects: { id: number; name: string }[] }>();
  const intervenantsById = new Map<number, string>();
  const projectProgramById = new Map<number, number>();

  const ensureProgram = (programId: number, fallbackName?: string | null) => {
    if (!Number.isFinite(programId) || programId <= 0) {
      return null;
    }

    const existingProgram = programsById.get(programId);
    if (existingProgram) {
      if (
        fallbackName &&
        (!existingProgram.name || isProgramFallbackName(existingProgram.name))
      ) {
        existingProgram.name = cleanName(fallbackName);
      }

      return existingProgram;
    }

    const program = {
      name: cleanName(fallbackName) || `Programme #${programId}`,
      projects: [],
    };
    programsById.set(programId, program);
    return program;
  };

  const ensureProject = (
    projectId: number,
    programId?: number | null,
    fallbackName?: string | null,
  ) => {
    if (!Number.isFinite(projectId) || projectId <= 0) {
      return;
    }

    const resolvedProgramId = Number(programId ?? projectProgramById.get(projectId) ?? 0);
    const program = ensureProgram(resolvedProgramId, null);

    if (!program) {
      return;
    }

    const existingProject = program.projects.find((project) => Number(project.id) === projectId);

    if (existingProject) {
      if (
        fallbackName &&
        (!existingProject.name || isProjectFallbackName(existingProject.name))
      ) {
        existingProject.name = cleanName(fallbackName);
      }
    } else {
      program.projects.push({
        id: projectId,
        name: cleanName(fallbackName) || `Projet #${projectId}`,
      });
    }

    projectProgramById.set(projectId, resolvedProgramId);
  };

  carrierTree.forEach((carrier) => {
    const carrierIntervenants = directIntervenantsOnly && Number(carrier.id) !== Number(currentUserId ?? 0)
      ? []
      : carrier.intervenants ?? [];

    carrierIntervenants.forEach((intervenant) => {
      if (!Number.isFinite(Number(intervenant?.id)) || Number(intervenant.id) <= 0) {
        return;
      }

      if (intervenant?.coworker === true) {
        return;
      }

      if (intervenant?.type === "intervenant") {
        return;
      }

      const intervenantName = getIntervenantDisplayName(intervenant);

      intervenantsById.set(
        Number(intervenant.id),
        intervenantName || `Intervenant #${intervenant.id}`,
      );
    });

    carrier.programs?.forEach((program) => {
      programsById.set(program.id, {
        name: cleanName(program.name),
        projects: (program.projects ?? []).map((project) => {
          projectProgramById.set(Number(project.id), Number(program.id));

          return {
            id: project.id,
            name: cleanName(project.name),
          };
        }),
      });
    });
  });

  Object.values(documentsData ?? {})
    .flat()
    .forEach((doc) => {
      const documentProgramId = Number(doc.program ?? 0);
      const documentProjectId = Number(doc.project ?? 0);
      const documentProgramName = getRecordText(doc, ["programName", "program_name", "programLabel", "program_label"]);
      const documentProjectName = getRecordText(doc, ["projectName", "project_name", "projectLabel", "project_label"]);

      if (Number.isFinite(documentProgramId) && documentProgramId > 0) {
        ensureProgram(documentProgramId, documentProgramName);
      }

      if (Number.isFinite(documentProjectId) && documentProjectId > 0) {
        ensureProject(
          documentProjectId,
          documentProgramId || projectProgramById.get(documentProjectId),
          documentProjectName,
        );
      }

      const documentSubcontractorId = Number(doc.subcontractorId ?? 0);
      const documentSubcontractorName = cleanName(doc.subcontractorName);
      if (
        Number.isFinite(documentSubcontractorId) &&
        documentSubcontractorId > 0 &&
        documentSubcontractorName &&
        !isIntervenantFallbackName(documentSubcontractorName)
      ) {
        const currentName = intervenantsById.get(documentSubcontractorId);
        if (!currentName || isIntervenantFallbackName(currentName)) {
          intervenantsById.set(documentSubcontractorId, documentSubcontractorName);
        }
      }

      (doc.intervenants ?? []).forEach((intervenant) => {
        const intervenantId = Number(intervenant?.id);

        if (!Number.isFinite(intervenantId) || intervenantId <= 0 || intervenant?.coworker === true) {
          return;
        }

        const intervenantName = getIntervenantDisplayName(intervenant);
        const currentName = intervenantsById.get(intervenantId);

        if (!currentName || currentName === `Intervenant #${intervenantId}`) {
          intervenantsById.set(
            intervenantId,
            intervenantName || `Intervenant #${intervenantId}`,
          );
        }
      });

      (doc.documentFolders ?? [])
        .filter((folder) => folder?.scopeType === "intervenant")
        .forEach((folder) => {
          const intervenantId = Number(folder?.scopeId ?? 0);

          if (!Number.isFinite(intervenantId) || intervenantId <= 0) {
            return;
          }

          const currentName = intervenantsById.get(intervenantId);
          const folderName = cleanName(folder?.name);
          const nextName =
            folderName && !isIntervenantFallbackName(folderName)
              ? folderName
              : `Intervenant #${intervenantId}`;

          if (!currentName || isIntervenantFallbackName(currentName)) {
            intervenantsById.set(intervenantId, nextName);
          }
        });
    });

  const currentActiveProgramId = Number(activeProgramId ?? 0);
  const currentActiveProjectId = Number(activeProjectId ?? 0);
  const currentActiveIntervenantId = Number(activeIntervenantId ?? 0);

  if (currentActiveProgramId > 0) {
    ensureProgram(currentActiveProgramId, null);
  }

  if (currentActiveProjectId > 0) {
    ensureProject(currentActiveProjectId, currentActiveProgramId || projectProgramById.get(currentActiveProjectId), null);
  }

  if (currentActiveIntervenantId > 0 && !intervenantsById.has(currentActiveIntervenantId)) {
    intervenantsById.set(currentActiveIntervenantId, `Intervenant #${currentActiveIntervenantId}`);
  }

  Object.keys(customFolders?.project ?? {})
    .forEach((projectId) => {
      const numericId = Number(projectId);

      if (!Number.isFinite(numericId) || numericId <= 0) {
        return;
      }

      const programId =
        projectProgramById.get(numericId) ??
        (numericId === currentActiveProjectId ? currentActiveProgramId : 0);

      ensureProject(numericId, programId, null);
    });

  Object.keys(customFolders?.intervenant ?? {})
    .forEach((intervenantId) => {
      const numericId = Number(intervenantId);

      if (!Number.isFinite(numericId) || numericId <= 0 || intervenantsById.has(numericId)) {
        return;
      }

      intervenantsById.set(numericId, `Intervenant #${numericId}`);
    });

  const mesProjetsChildren = [...programsById.entries()].flatMap(([programId, program]) => {
    const programDefaultKey = getProgramDefaultKey(programId);
    const rawCustomProgramFolderData = sortCustomFolders(
      getScopeFolders(customFolders?.porteur, currentUserId).filter(
        (folder) => folder.defaultKey === programDefaultKey,
      ),
    );
    const programRootHidden = isDefaultHidden(
      customFolders?.hiddenDefaults,
      "porteur",
      currentUserId ?? 0,
      programDefaultKey,
    );
    const {
      overrideFolder: programRootOverride,
      remainingFolders: customProgramFolders,
    } = consumePersistedDefaultOverrideFolder(
      rawCustomProgramFolderData,
      programDefaultKey,
      [],
      programRootHidden,
    );

    const isProgramRootVisible =
      !programRootHidden ||
      Boolean(programRootOverride) ||
      Number(programId) === currentActiveProgramId;

    if (!isProgramRootVisible) {
      return [];
    }

    return [{
      id: `program-${programId}`,
      label: getFolderDisplayName(programRootOverride, program.name),
      url: getUrl({ programs: [programId] }),
      children: [
        ...program.projects.map((project) => {
          const rawCustomProjectFolderData = getScopeFolders(customFolders?.project, project.id);
          const projectRootHidden = isDefaultHidden(customFolders?.hiddenDefaults, "project", project.id, "root");
          const {
            overrideFolder: projectRootOverride,
            remainingFolders: customProjectFoldersAfterRoot,
          } = consumePersistedDefaultOverrideFolder(
            rawCustomProjectFolderData,
            "root",
            [],
            projectRootHidden,
          );
          const customProjectTypeOverrides = new Map<number, CustomDocumentFolderNode | null>();
          let customProjectFoldersWithoutDefaults = customProjectFoldersAfterRoot;

          PROJECT_DOC_TYPES.forEach((documentType) => {
            const defaultHidden = isDefaultHidden(
              customFolders?.hiddenDefaults,
              "project",
              project.id,
              `type-${documentType}`,
            );
            const { overrideFolder, remainingFolders } = consumePersistedDefaultOverrideFolder(
              customProjectFoldersWithoutDefaults,
              `type-${documentType}`,
              [documentType],
              defaultHidden,
            );
            customProjectTypeOverrides.set(documentType, overrideFolder);
            customProjectFoldersWithoutDefaults = remainingFolders;
          });
          const projectPhotosHidden = isDefaultHidden(
            customFolders?.hiddenDefaults,
            "project",
            project.id,
            "photos",
          );
          const {
            overrideFolder: projectPhotosOverride,
            remainingFolders: foldersWithoutProjectPhotosOverride,
          } = consumePersistedDefaultOverrideFolder(
            customProjectFoldersWithoutDefaults,
            "photos",
            [],
            projectPhotosHidden,
          );
          customProjectFoldersWithoutDefaults = foldersWithoutProjectPhotosOverride;

          const sortedCustomProjectFolderData = sortCustomFolders(customProjectFoldersWithoutDefaults);
          const customProjectFolders = buildCustomFolderNodes({
            kind: "project",
            ownerId: project.id,
            programId,
            folders: sortCustomFolders([
              ...(projectRootOverride?.children ?? []),
              ...sortedCustomProjectFolderData,
            ]),
          });

          const children: DocumentTreeNode[] = [
            ...PROJECT_DOC_TYPES
              .filter((documentType) => {
                const defaultKey = `type-${documentType}`;
                return (
                  !isDefaultHidden(customFolders?.hiddenDefaults, "project", project.id, defaultKey) ||
                  Boolean(customProjectTypeOverrides.get(documentType))
                );
              })
              .map((documentType) => ({
                id: `project-${project.id}-type-${documentType}`,
                label: getFolderDisplayName(
                  customProjectTypeOverrides.get(documentType),
                  LABEL_BY_TYPE[documentType],
                ),
                url: getUrl({
                  programs: [programId],
                  projects: [project.id],
                  categories: [documentType],
                }),
                children: buildCustomFolderNodes({
                  kind: "project",
                  ownerId: project.id,
                  programId,
                  folders: sortCustomFolders([
                    ...(customProjectTypeOverrides.get(documentType)?.children ?? []),
                    ...filterFoldersByDocumentTypes(sortedCustomProjectFolderData, [documentType]),
                  ]),
                }),
                deleteFolderTarget: {
                  kind: "default",
                  defaultKey: `type-${documentType}`,
                  scopeType: "project",
                  scopeId: project.id,
                },
              })),
            ...customProjectFolders,
            ...(!projectPhotosHidden || projectPhotosOverride ? [{
              id: `project-${project.id}-photos`,
              label: getFolderDisplayName(projectPhotosOverride, "Photos"),
              url: getUrl({
                programs: [programId],
                projects: [project.id],
                types: ["image"],
              }),
              children: buildCustomFolderNodes({
                kind: "project",
                ownerId: project.id,
                programId,
                folders: sortCustomFolders(projectPhotosOverride?.children ?? []),
              }),
              deleteFolderTarget: {
                kind: "default" as const,
                defaultKey: "photos",
                scopeType: "project" as const,
                scopeId: project.id,
              },
            }] : []),
          ];

          const isProjectRootVisible =
            !projectRootHidden ||
            Boolean(projectRootOverride) ||
            Number(project.id) === currentActiveProjectId;

          if (!isProjectRootVisible) {
            return null;
          }

          return {
            id: `project-${project.id}`,
            label:
              projectRootOverride?.name &&
              !isProjectFallbackName(projectRootOverride.name) &&
              !isUnknownDocumentLabel(projectRootOverride.name)
                ? projectRootOverride.name
                : project.name,
            url: getUrl({ programs: [programId], projects: [project.id] }),
            children,
            deleteFolderTarget: {
              kind: "default",
              defaultKey: "root",
              scopeType: "project",
              scopeId: project.id,
            },
            addFolderTarget: {
              kind: "project",
              id: project.id,
              programId,
            },
          };
        }).filter(Boolean) as DocumentTreeNode[],
        ...buildCustomFolderNodes({
          kind: "porteur",
          ownerId: currentUserId ?? 0,
          folders: sortCustomFolders([
            ...(programRootOverride?.children ?? []),
            ...customProgramFolders,
          ]),
          extraParams: { programs: [programId] },
        }),
      ],
      deleteFolderTarget:
        currentUserId && currentUserId > 0
          ? {
            kind: "default",
            defaultKey: programDefaultKey,
            scopeType: "porteur",
            scopeId: currentUserId,
          }
          : undefined,
    }];
  });

  const dossierIntervenantsChildren = [...intervenantsById.entries()]
    .sort((left, right) => String(left[1]).localeCompare(String(right[1]), "fr"))
    .flatMap(([intervenantId, intervenantName]) => {
      const rawCustomIntervenantFolderData = getScopeFolders(customFolders?.intervenant, intervenantId);
      const intervenantRootHidden = isDefaultHidden(customFolders?.hiddenDefaults, "intervenant", intervenantId, "root");
      const {
        overrideFolder: intervenantRootOverride,
        remainingFolders: customIntervenantFoldersAfterRoot,
      } = consumePersistedDefaultOverrideFolder(
        rawCustomIntervenantFolderData,
        "root",
        [],
        intervenantRootHidden,
      );
      const intervenantTypeOverrides = new Map<number, CustomDocumentFolderNode | null>();
      let customIntervenantFoldersWithoutDefaults = customIntervenantFoldersAfterRoot;

      INTERVENANT_DOC_TYPES.forEach((documentType) => {
        const defaultHidden = isDefaultHidden(
          customFolders?.hiddenDefaults,
          "intervenant",
          intervenantId,
          `type-${documentType}`,
        );
        const { overrideFolder, remainingFolders } = consumePersistedDefaultOverrideFolder(
          customIntervenantFoldersWithoutDefaults,
          `type-${documentType}`,
          [documentType],
          defaultHidden,
        );
        intervenantTypeOverrides.set(documentType, overrideFolder);
        customIntervenantFoldersWithoutDefaults = remainingFolders;
      });

      const intervenantContractualHidden = isDefaultHidden(customFolders?.hiddenDefaults, "intervenant", intervenantId, "contractuel");
      const {
        overrideFolder: intervenantContractualOverride,
        remainingFolders: customIntervenantFoldersAfterContractual,
      } = consumePersistedDefaultOverrideFolder(
        customIntervenantFoldersWithoutDefaults,
        "contractuel",
        CONTRACTUAL_DOC_TYPES,
        intervenantContractualHidden,
      );
      const intervenantReportHidden = isDefaultHidden(customFolders?.hiddenDefaults, "intervenant", intervenantId, "rapport");
      const {
        overrideFolder: intervenantReportOverride,
        remainingFolders: customIntervenantFolderData,
      } = consumePersistedDefaultOverrideFolder(
        customIntervenantFoldersAfterContractual,
        "rapport",
        REPORT_DOC_TYPES,
        intervenantReportHidden,
      );

      const sortedCustomIntervenantFolderData = sortCustomFolders(customIntervenantFolderData);
      const customIntervenantFolders = buildCustomFolderNodes({
        kind: "intervenant",
        ownerId: intervenantId,
        folders: sortCustomFolders([
          ...(intervenantRootOverride?.children ?? []),
          ...sortedCustomIntervenantFolderData,
        ]),
      });

      const isIntervenantRootVisible = !intervenantRootHidden || Boolean(intervenantRootOverride);

      if (!isIntervenantRootVisible) {
        return [];
      }

      const children: DocumentTreeNode[] = [
        ...INTERVENANT_DOC_TYPES
          .filter((documentType) => {
            const defaultKey = `type-${documentType}`;
            return (
              !isDefaultHidden(customFolders?.hiddenDefaults, "intervenant", intervenantId, defaultKey) ||
              Boolean(intervenantTypeOverrides.get(documentType))
            );
          })
          .map((documentType) => ({
            id: `intervenant-${intervenantId}-type-${documentType}`,
            label: getFolderDisplayName(
              intervenantTypeOverrides.get(documentType),
              LABEL_BY_TYPE[documentType],
            ),
            url: getUrl({
              origin: "PROGRAM",
              intervenants: [intervenantId],
              categories: [documentType],
            }),
            children: buildCustomFolderNodes({
              kind: "intervenant",
              ownerId: intervenantId,
              folders: sortCustomFolders([
                ...(intervenantTypeOverrides.get(documentType)?.children ?? []),
                ...filterFoldersByDocumentTypes(sortedCustomIntervenantFolderData, [documentType]),
              ]),
            }),
            deleteFolderTarget: {
              kind: "default",
              defaultKey: `type-${documentType}`,
              scopeType: "intervenant",
              scopeId: intervenantId,
            },
          })),
        ...customIntervenantFolders,
      ];

      if (
        !isDefaultHidden(customFolders?.hiddenDefaults, "intervenant", intervenantId, "contractuel") ||
        intervenantContractualOverride
      ) {
        children.push({
          id: `intervenant-${intervenantId}-contractuel`,
          label: getFolderDisplayName(intervenantContractualOverride, "Contractuel"),
          url: getUrl({
            origin: "PROGRAM",
            intervenants: [intervenantId],
            categories: CONTRACTUAL_DOC_TYPES,
          }),
          children: buildCustomFolderNodes({
            kind: "intervenant",
            ownerId: intervenantId,
            folders: sortCustomFolders([
              ...(intervenantContractualOverride?.children ?? []),
              ...filterFoldersByDocumentTypes(sortedCustomIntervenantFolderData, CONTRACTUAL_DOC_TYPES),
            ]),
          }),
          deleteFolderTarget: {
            kind: "default",
            defaultKey: "contractuel",
            scopeType: "intervenant",
            scopeId: intervenantId,
          },
        });
      }

      if (
        !isDefaultHidden(customFolders?.hiddenDefaults, "intervenant", intervenantId, "rapport") ||
        intervenantReportOverride
      ) {
        children.push({
          id: `intervenant-${intervenantId}-rapport`,
          label: getFolderDisplayName(intervenantReportOverride, "Rapport"),
          url: getUrl({
            origin: "PROGRAM",
            intervenants: [intervenantId],
            categories: REPORT_DOC_TYPES,
          }),
          children: buildCustomFolderNodes({
            kind: "intervenant",
            ownerId: intervenantId,
            folders: sortCustomFolders([
              ...(intervenantReportOverride?.children ?? []),
              ...filterFoldersByDocumentTypes(sortedCustomIntervenantFolderData, REPORT_DOC_TYPES),
            ]),
          }),
          deleteFolderTarget: {
            kind: "default",
            defaultKey: "rapport",
            scopeType: "intervenant",
            scopeId: intervenantId,
          },
        });
      }

      return [{
        id: `intervenant-${intervenantId}`,
        label:
          intervenantRootOverride?.name &&
          !isIntervenantFallbackName(intervenantRootOverride.name) &&
          !isUnknownDocumentLabel(intervenantRootOverride.name)
            ? intervenantRootOverride.name
            : intervenantName,
        url: getUrl({
          origin: "PROGRAM",
          intervenants: [intervenantId],
        }),
        children,
        addFolderTarget: {
          kind: "intervenant",
          id: intervenantId,
        },
        deleteFolderTarget: {
          kind: "default",
          defaultKey: "root",
          scopeType: "intervenant",
          scopeId: intervenantId,
        },
      }];
    });

  const rawCustomPorteurFolderData = getScopeFolders(customFolders?.porteur, currentUserId);
  const projectsRootHidden = isDefaultHidden(
    customFolders?.hiddenDefaults,
    "porteur",
    currentUserId ?? 0,
    PROJECTS_ROOT_DEFAULT_KEY,
  );
  const {
    overrideFolder: projectsRootOverride,
    remainingFolders: customPorteurFoldersWithProjectsRootChildren,
  } = consumePersistedDefaultOverrideFolder(
    rawCustomPorteurFolderData,
    PROJECTS_ROOT_DEFAULT_KEY,
    [],
    projectsRootHidden,
  );
  const {
    matchedFolders: projectsRootCustomFolders,
    remainingFolders: customPorteurFoldersAfterProjectsRoot,
  } = partitionFolders(
    customPorteurFoldersWithProjectsRootChildren,
    (folder) => folder.defaultKey === PROJECTS_ROOT_DEFAULT_KEY,
  );
  const intervenantsRootHidden = isDefaultHidden(
    customFolders?.hiddenDefaults,
    "porteur",
    currentUserId ?? 0,
    INTERVENANTS_ROOT_DEFAULT_KEY,
  );
  const {
    overrideFolder: intervenantsRootOverride,
    remainingFolders: customPorteurFoldersWithIntervenantsRootChildren,
  } = consumePersistedDefaultOverrideFolder(
    customPorteurFoldersAfterProjectsRoot,
    INTERVENANTS_ROOT_DEFAULT_KEY,
    [],
    intervenantsRootHidden,
  );
  const {
    matchedFolders: intervenantsRootCustomFolders,
    remainingFolders: customPorteurFoldersBeforeRoot,
  } = partitionFolders(
    customPorteurFoldersWithIntervenantsRootChildren,
    (folder) => folder.defaultKey === INTERVENANTS_ROOT_DEFAULT_KEY,
  );
  const porteurRootHidden = isDefaultHidden(customFolders?.hiddenDefaults, "porteur", currentUserId ?? 0, "root");
  const {
    overrideFolder: porteurRootOverride,
    remainingFolders: customPorteurFoldersAfterRoot,
  } = consumePersistedDefaultOverrideFolder(
    customPorteurFoldersBeforeRoot,
    "root",
    [],
    porteurRootHidden,
  );
  const porteurTypeOverrides = new Map<number, CustomDocumentFolderNode | null>();
  let customPorteurFoldersWithoutDefaults = customPorteurFoldersAfterRoot;

  INTERVENANT_DOC_TYPES.forEach((documentType) => {
    const defaultHidden = isDefaultHidden(
      customFolders?.hiddenDefaults,
      "porteur",
      currentUserId ?? 0,
      `type-${documentType}`,
    );
    const { overrideFolder, remainingFolders } = consumePersistedDefaultOverrideFolder(
      customPorteurFoldersWithoutDefaults,
      `type-${documentType}`,
      [documentType],
      defaultHidden,
    );
    porteurTypeOverrides.set(documentType, overrideFolder);
    customPorteurFoldersWithoutDefaults = remainingFolders;
  });

  const porteurContractualHidden = isDefaultHidden(customFolders?.hiddenDefaults, "porteur", currentUserId ?? 0, "contractuel");
  const {
    overrideFolder: porteurContractualOverride,
    remainingFolders: customPorteurFoldersAfterContractual,
  } = consumePersistedDefaultOverrideFolder(
    customPorteurFoldersWithoutDefaults,
    "contractuel",
    CONTRACTUAL_DOC_TYPES,
    porteurContractualHidden,
  );
  const porteurReportHidden = isDefaultHidden(customFolders?.hiddenDefaults, "porteur", currentUserId ?? 0, "rapport");
  const {
    overrideFolder: porteurReportOverride,
    remainingFolders: customPorteurFolderData,
  } = consumePersistedDefaultOverrideFolder(
    customPorteurFoldersAfterContractual,
    "rapport",
    REPORT_DOC_TYPES,
    porteurReportHidden,
  );

  const sortedCustomPorteurFolderData = sortCustomFolders(customPorteurFolderData);
  const orphanProgramRootNodes: DocumentTreeNode[] = sortedCustomPorteurFolderData
    .filter((folder) => {
      const defaultKey = folder.defaultKey ?? "";
      if (!defaultKey.startsWith(PROGRAM_ROOT_DEFAULT_KEY_PREFIX)) {
        return false;
      }

      const programId = Number(defaultKey.slice(PROGRAM_ROOT_DEFAULT_KEY_PREFIX.length));
      return Number.isFinite(programId) && programId > 0 && !programsById.has(programId);
    })
    .map((folder) => ({
      id: `orphan-program-folder-${folder.id}`,
      label: folder.name || "Programme archive",
      url: getUrl(getFolderUrlParams(folder, { scope: "projects" })),
      children: buildCustomFolderNodes({
        kind: "porteur",
        ownerId: currentUserId ?? 0,
        folders: folder.children,
        extraParams: { scope: "projects" },
      }),
      deleteFolderTarget:
        currentUserId && currentUserId > 0
          ? {
              kind: "default",
              defaultKey: folder.defaultKey ?? undefined,
              scopeType: "porteur",
              scopeId: currentUserId,
            }
          : undefined,
      addFolderTarget:
        currentUserId && currentUserId > 0
          ? {
              kind: "porteur",
              id: currentUserId,
              parentFolderId: folder.id,
            }
          : undefined,
    }));
  const customPorteurFolders = buildCustomFolderNodes({
    kind: "porteur",
    ownerId: currentUserId ?? 0,
    folders: sortedCustomPorteurFolderData.filter(
      (folder) =>
        !(folder.defaultKey ?? "").startsWith(PROGRAM_ROOT_DEFAULT_KEY_PREFIX) &&
        (folder.defaultKey ?? null) !== ROOT_FILES_DEFAULT_KEY,
    ),
  });

  const porteurProjetChildren: DocumentTreeNode[] = [
    ...INTERVENANT_DOC_TYPES
      .filter((type) => {
        const defaultKey = `type-${type}`;
        return (
          !isDefaultHidden(customFolders?.hiddenDefaults, "porteur", currentUserId ?? 0, defaultKey) ||
          Boolean(porteurTypeOverrides.get(type))
        );
      })
      .map((type) => ({
        id: `porteur-projet-type-${type}`,
        label: getFolderDisplayName(porteurTypeOverrides.get(type), LABEL_BY_TYPE[type]),
        url: getUrl({
          categories: [type],
        }),
        children: buildCustomFolderNodes({
          kind: "porteur",
          ownerId: currentUserId ?? 0,
          folders: sortCustomFolders([
            ...(porteurTypeOverrides.get(type)?.children ?? []),
            ...filterFoldersByDocumentTypes(sortedCustomPorteurFolderData, [type]),
          ]),
        }),
        deleteFolderTarget: {
          kind: "default",
          defaultKey: `type-${type}`,
          scopeType: "porteur",
          scopeId: currentUserId ?? 0,
        },
      })),
    ...(
      !isDefaultHidden(customFolders?.hiddenDefaults, "porteur", currentUserId ?? 0, "contractuel") ||
        porteurContractualOverride
        ? [{
          id: "porteur-projet-contractuel",
          label: getFolderDisplayName(porteurContractualOverride, "Contractuel"),
          url: getUrl({
            categories: CONTRACTUAL_DOC_TYPES,
          }),
          children: buildCustomFolderNodes({
            kind: "porteur",
            ownerId: currentUserId ?? 0,
            folders: sortCustomFolders([
              ...(porteurContractualOverride?.children ?? []),
              ...filterFoldersByDocumentTypes(sortedCustomPorteurFolderData, CONTRACTUAL_DOC_TYPES),
            ]),
          }),
          deleteFolderTarget: {
            kind: "default" as const,
            defaultKey: "contractuel",
            scopeType: "porteur" as const,
            scopeId: currentUserId ?? 0,
          },
        }]
        : []
    ),
    ...(
      !isDefaultHidden(customFolders?.hiddenDefaults, "porteur", currentUserId ?? 0, "rapport") ||
        porteurReportOverride
        ? [{
          id: "porteur-projet-rapport",
          label: getFolderDisplayName(porteurReportOverride, "Rapport"),
          url: getUrl({
            categories: REPORT_DOC_TYPES,
          }),
          children: buildCustomFolderNodes({
            kind: "porteur",
            ownerId: currentUserId ?? 0,
            folders: sortCustomFolders([
              ...(porteurReportOverride?.children ?? []),
              ...filterFoldersByDocumentTypes(sortedCustomPorteurFolderData, REPORT_DOC_TYPES),
            ]),
          }),
          deleteFolderTarget: {
            kind: "default" as const,
            defaultKey: "rapport",
            scopeType: "porteur" as const,
            scopeId: currentUserId ?? 0,
          },
        }]
        : []
    ),
  ];

  const rootNodes: DocumentTreeNode[] = [];

  if (!projectsRootHidden || projectsRootOverride) {
    rootNodes.push({
      id: "mes-projets",
      label:
        projectsRootOverride?.name?.trim().toLowerCase() === "mes projets"
          ? "Mes Programmes"
          : getFolderDisplayName(projectsRootOverride, "Mes Programmes"),
      url: getUrl({ scope: "projects" }),
      children: [
        ...mesProjetsChildren,
        ...orphanProgramRootNodes,
        ...buildCustomFolderNodes({
          kind: "porteur",
          ownerId: currentUserId ?? 0,
          folders: sortCustomFolders([
            ...(projectsRootOverride?.children ?? []),
            ...projectsRootCustomFolders,
          ]),
          extraParams: { scope: "projects" },
        }),
      ],
      deleteFolderTarget:
        currentUserId && currentUserId > 0
          ? {
            kind: "default",
            defaultKey: PROJECTS_ROOT_DEFAULT_KEY,
            scopeType: "porteur",
            scopeId: currentUserId,
          }
          : undefined,
    });
  }

  if ((!hideIntervenantFolder || currentActiveIntervenantId > 0) && (!intervenantsRootHidden || intervenantsRootOverride)) {
    rootNodes.push({
      id: "dossier-intervenants",
      label: getFolderDisplayName(intervenantsRootOverride, "Dossier intervenant"),
      url: getUrl({ scope: "intervenants" }),
      children: [
        ...dossierIntervenantsChildren,
        ...buildCustomFolderNodes({
          kind: "porteur",
          ownerId: currentUserId ?? 0,
          folders: sortCustomFolders([
            ...(intervenantsRootOverride?.children ?? []),
            ...intervenantsRootCustomFolders,
          ]),
          extraParams: { scope: "intervenants" },
        }),
      ],
      deleteFolderTarget:
        currentUserId && currentUserId > 0
          ? {
            kind: "default",
            defaultKey: INTERVENANTS_ROOT_DEFAULT_KEY,
            scopeType: "porteur",
            scopeId: currentUserId,
          }
          : undefined,
    });
  }

  if (!porteurRootHidden || porteurRootOverride) {
    rootNodes.push({
      id: "porteur-projet",
      label: getFolderDisplayName(porteurRootOverride, PORTEUR_ROOT_DEFAULT_LABEL),
      url: getUrl({ scope: "porteur" }),
      deleteFolderTarget:
        currentUserId && currentUserId > 0
          ? {
            kind: "default",
            defaultKey: "root",
            scopeType: "porteur",
            scopeId: currentUserId,
          }
          : undefined,
      addFolderTarget:
        currentUserId && currentUserId > 0
          ? {
            kind: "porteur",
            id: currentUserId,
          }
          : undefined,
      children: [
        ...porteurProjetChildren,
        ...buildCustomFolderNodes({
          kind: "porteur",
          ownerId: currentUserId ?? 0,
          folders: sortCustomFolders(porteurRootOverride?.children ?? []),
          extraParams: { scope: "porteur" },
        }),
      ],
    });
  }

  return [
    ...rootNodes,
    ...customPorteurFolders,
  ];
};

export const buildDocumentBreadcrumb = ({
  carrierTree = [],
  customFolders,
  searchParams,
  currentUserId,
  documentsData,
}: {
  carrierTree?: TreeRoot[];
  customFolders?: DocumentFolderTreeResponse;
  searchParams: URLSearchParams;
  currentUserId?: number | null;
  documentsData?: Record<string, Array<Partial<DocumentRecord> & Record<string, unknown>>> | null;
}): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [];
  const scope = searchParams.get("scope");
  const programId = Number(searchParams.get("programs")?.split(",")[0] ?? "");
  const projectId = Number(searchParams.get("projects")?.split(",")[0] ?? "");
  const intervenantId = Number(searchParams.get("intervenants")?.split(",")[0] ?? "");
  const folderId = Number(searchParams.get("folderIds")?.split(",")[0] ?? "");
  const categoryId = Number(searchParams.get("categories")?.split(",")[0] ?? "");
  const categoryIds = (searchParams.get("categories") ?? "")
    .split(",")
    .map(Number)
    .filter((value) => Number.isFinite(value) && value > 0);

  const allPrograms = carrierTree.flatMap((carrier) => carrier.programs ?? []);
  const allDocuments = Object.values(documentsData ?? {}).flat();
  const documentForProgram = allDocuments.find((doc) => Number(doc.program ?? 0) === programId);
  const documentForProject = allDocuments.find((doc) => Number(doc.project ?? 0) === projectId);
  const program = allPrograms.find((candidate) => candidate.id === programId);
  const project = program?.projects?.find((candidate) => candidate.id === projectId)
    ?? allPrograms.flatMap((candidate) => candidate.projects ?? []).find((candidate) => candidate.id === projectId);
  const inferredProgramId = Number(documentForProject?.program ?? documentForProgram?.program ?? 0);
  const resolvedProgramId = Number(program?.id ?? (programId || inferredProgramId));
  const resolvedProjectId = Number(project?.id ?? projectId);
  const programName =
    cleanName(program?.name) ||
    cleanName(getDocumentProgramName(documentForProgram ?? documentForProject ?? {})) ||
    (resolvedProgramId > 0 ? `Programme #${resolvedProgramId}` : "");
  const projectName =
    cleanName(project?.name) ||
    cleanName(getDocumentProjectName(documentForProject ?? {})) ||
    (resolvedProjectId > 0 ? `Projet #${resolvedProjectId}` : "");
  const intervenant = carrierTree
    .flatMap((carrier) => carrier.intervenants ?? [])
    .find((candidate) => Number(candidate.id) === intervenantId);

  if (scope === "projects" && !program && !project) {
    breadcrumbs.push({
      name: getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: PROJECTS_ROOT_DEFAULT_KEY,
        fallbackLabel: "Mes Programmes",
      }),
      href: getUrl({ scope: "projects" }),
    });
  }

  if (scope === "intervenants" && !intervenant) {
    breadcrumbs.push({
      name: getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: INTERVENANTS_ROOT_DEFAULT_KEY,
        fallbackLabel: "Dossier intervenant",
      }),
      href: getUrl({ scope: "intervenants" }),
    });
  }

  if (scope === "porteur" && !program && !project && !intervenant) {
    breadcrumbs.push({
      name: getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: "root",
        fallbackLabel: PORTEUR_ROOT_DEFAULT_LABEL,
      }),
      href: getUrl({ scope: "porteur" }),
    });
  }

  if (program || project || resolvedProgramId > 0 || resolvedProjectId > 0) {
    breadcrumbs.push({
      name: getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: PROJECTS_ROOT_DEFAULT_KEY,
        fallbackLabel: "Mes Programmes",
      }),
      href: getUrl({ scope: "projects" }),
    });
  }

  if (intervenant || intervenantId > 0) {
    breadcrumbs.push({
      name: getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: INTERVENANTS_ROOT_DEFAULT_KEY,
        fallbackLabel: "Dossier intervenant",
      }),
      href: getUrl({ scope: "intervenants" }),
    });
  }

  if (
    !scope &&
    !program &&
    !project &&
    !intervenant &&
    !resolvedProgramId &&
    !resolvedProjectId &&
    !intervenantId &&
    categoryId
  ) {
    breadcrumbs.push({
      name: getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "porteur",
        scopeId: currentUserId ?? 0,
        defaultKey: "root",
        fallbackLabel: PORTEUR_ROOT_DEFAULT_LABEL,
      }),
      href: getUrl({ scope: "porteur" }),
    });
  }

  if (resolvedProgramId > 0) {
    breadcrumbs.push({
      name: getProgramDisplayLabel({
        customFolders,
        currentUserId,
        programId: resolvedProgramId,
        fallbackLabel: programName,
      }),
      href: getUrl({ programs: [resolvedProgramId] }),
    });
  }

  if (resolvedProjectId > 0) {
    breadcrumbs.push({
      name: getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "project",
        scopeId: resolvedProjectId,
        defaultKey: "root",
        fallbackLabel: projectName,
      }),
      href: getUrl({
        programs: resolvedProgramId > 0 ? [resolvedProgramId] : undefined,
        projects: [resolvedProjectId],
      }),
    });
  }

  if (intervenant || intervenantId > 0) {
    const intervenantName = getIntervenantDisplayName(intervenant) || `Intervenant #${intervenantId}`;

    breadcrumbs.push({
      name: getDefaultFolderDisplayLabel({
        customFolders,
        scopeType: "intervenant",
        scopeId: Number(intervenant?.id ?? intervenantId),
        defaultKey: "root",
        fallbackLabel: intervenantName,
      }),
      href: getUrl({
        origin: "PROGRAM",
        intervenants: [Number(intervenant?.id ?? intervenantId)],
      }),
    });
  }

  if (folderId && projectId) {
    const folderPath = findCustomFolderPath(getScopeFolders(customFolders?.project, projectId), folderId) ?? [];
    const visibleFolderPath = folderPath.filter(
      (folder, index) => !(index === 0 && (folder.defaultKey ?? null) === "root"),
    );
    visibleFolderPath.forEach((folder) => {
      breadcrumbs.push({
        name: folder.name,
        href: getUrl({
          programs: resolvedProgramId > 0 ? [resolvedProgramId] : undefined,
          projects: [projectId],
          ...(getFolderDocumentTypes(folder).length > 0 ? { categories: getFolderDocumentTypes(folder) } : {}),
          folderIds: [folder.id],
        }),
      });
    });
  }

  if (folderId && intervenantId) {
    const folderPath = findCustomFolderPath(getScopeFolders(customFolders?.intervenant, intervenantId), folderId) ?? [];
    const visibleFolderPath = folderPath.filter(
      (folder, index) => !(index === 0 && (folder.defaultKey ?? null) === "root"),
    );
    visibleFolderPath.forEach((folder) => {
      breadcrumbs.push({
        name: folder.name,
        href: getUrl({
          origin: "PROGRAM",
          intervenants: [intervenantId],
          ...(getFolderDocumentTypes(folder).length > 0 ? { categories: getFolderDocumentTypes(folder) } : {}),
          folderIds: [folder.id],
        }),
      });
    });
  }

  if (folderId && currentUserId && !projectId && !intervenantId) {
    const folderPath = findCustomFolderPath(getScopeFolders(customFolders?.porteur, currentUserId), folderId) ?? [];
    const rootDefaultKey = folderPath[0]?.defaultKey ?? null;
    const isPorteurDefaultTypePath =
      (rootDefaultKey?.startsWith("type-") ?? false) ||
      rootDefaultKey === "contractuel" ||
      rootDefaultKey === "rapport";
    const isPorteurProjectPath = rootDefaultKey === "root" || isPorteurDefaultTypePath;
    const inferredScope =
      scope ??
      (isPorteurProjectPath
        ? "porteur"
        : rootDefaultKey === PROJECTS_ROOT_DEFAULT_KEY
          ? "projects"
          : rootDefaultKey === INTERVENANTS_ROOT_DEFAULT_KEY
            ? "intervenants"
            : null);
    const hiddenContextDefaultKey =
      resolvedProgramId > 0
        ? getProgramDefaultKey(resolvedProgramId)
        : inferredScope === "projects"
          ? PROJECTS_ROOT_DEFAULT_KEY
          : inferredScope === "intervenants"
            ? INTERVENANTS_ROOT_DEFAULT_KEY
            : rootDefaultKey === "root"
              ? "root"
              : null;

    if (
      isPorteurProjectPath &&
      !breadcrumbs.some((item) => item.href === getUrl({ scope: "porteur" }))
    ) {
      breadcrumbs.push({
        name: getDefaultFolderDisplayLabel({
          customFolders,
          scopeType: "porteur",
          scopeId: currentUserId,
          defaultKey: "root",
          fallbackLabel: PORTEUR_ROOT_DEFAULT_LABEL,
        }),
        href: getUrl({ scope: "porteur" }),
      });
    }

    const visibleFolderPath = folderPath.filter((folder, index) => {
      if (index !== 0 || !hiddenContextDefaultKey) {
        return true;
      }

      return folder.defaultKey !== hiddenContextDefaultKey;
    });
    const baseFolderParams =
      resolvedProgramId > 0
        ? { programs: [resolvedProgramId] }
        : inferredScope === "projects" || inferredScope === "intervenants" || inferredScope === "porteur"
          ? { scope: inferredScope }
          : {};

    visibleFolderPath.forEach((folder) => {
      breadcrumbs.push({
        name: folder.name,
        href: getUrl({
          ...baseFolderParams,
          ...(getFolderDocumentTypes(folder).length > 0 ? { categories: getFolderDocumentTypes(folder) } : {}),
          folderIds: [folder.id],
        }),
      });
    });
  }

  if (!folderId && categoryId) {
    const defaultCategoryScope =
      resolvedProjectId > 0
        ? "project"
        : intervenantId > 0
          ? "intervenant"
          : "porteur";
    const defaultCategoryScopeId =
      resolvedProjectId > 0
        ? resolvedProjectId
        : intervenantId > 0
          ? intervenantId
          : currentUserId ?? 0;
    const defaultCategoryLabel = getDefaultCategoryDisplayLabel({
      customFolders,
      scopeType: defaultCategoryScope,
      scopeId: defaultCategoryScopeId,
      categoryIds: categoryIds.length > 0 ? categoryIds : [categoryId],
      fallbackLabel: getDocumentTypeLabel(categoryId),
    });

    breadcrumbs.push({
      name: defaultCategoryLabel,
      href: getUrl({
        ...(resolvedProgramId > 0 ? { programs: [resolvedProgramId] } : {}),
        ...(resolvedProjectId > 0 ? { projects: [resolvedProjectId] } : {}),
        ...(intervenantId > 0 ? { origin: "PROGRAM", intervenants: [intervenantId] } : {}),
        categories: categoryIds.length > 0 ? categoryIds : [categoryId],
      }),
    });
  }

  return breadcrumbs.map((item, index, items) =>
    index === items.length - 1 ? { name: item.name } : item,
  );
};
